import { useState, useRef, useEffect, useMemo, lazy, Suspense, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Brain, Target, Book, 
  Lightbulb, History, ChevronLeft, ChevronRight, Network, BookOpen, Share2, X, MessageCircle } from 'lucide-react';
import { LearningBlock } from '../components/bias/LearningBlock';
import { KeyPoint } from '../components/bias/KeyPoint';
import { CheckItem } from '../components/bias/CheckItem';
import { RelatedBiases } from '../components/bias/RelatedBiases';
import type { CognitiveBias } from '../types';
import React from 'react';
import { MarkdownContent } from '../components/ui/MarkdownContent';
import { Helmet } from 'react-helmet-async';
import { generateBiasDescription, generateBiasTitle, generateBiasKeywords, generateBiasStructuredData, SITE_URL } from '../utils/seo';
import { Lightbox } from '../components/ui/Lightbox';
import { simulations } from '../components/simulations';
import { Portal } from '../components/ui/Portal';
import { ShareModal } from '../components/ui/ShareModal';
import BiasChatButton from '../components/bias/BiasChatButton';

// Lazy load non-critical components
const BiasTest = lazy(() => import('../components/bias/BiasTest').then(module => ({ default: module.BiasTest })));
const BiasSimulation = lazy(() => import('../components/bias/BiasSimulation').then(module => ({ default: module.BiasSimulation })));

interface BiasPageProps {
  biases: CognitiveBias[];
}

// Add debounce utility at the top
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// First, let's add a transition duration constant
const TRANSITION_DURATION = 300; // matches the duration-300 in the CSS

// Add this helper function at the top of the component
const getRandomBiasesFromCategory = (currentBias: CognitiveBias, allBiases: CognitiveBias[], count: number = 2) => {
  // Filter biases from the same category, excluding the current bias
  const sameCategoryBiases = allBiases.filter(b => 
    b.category === currentBias.category && 
    b.id !== currentBias.id
  );

  // Shuffle and take first 2 biases
  return sameCategoryBiases
    .sort(() => Math.random() - 0.5)
    .slice(0, count);
};

// Add this helper function to get simulation title
const getSimulationTitle = (simulationId: string) => {
  switch (simulationId) {
    case 'anchoring':
      return 'The Anchoring Effect Experiment';
    default:
      return 'Psychological Study Simulation';
  }
};

// Add this helper function to check if a valid simulation exists
const hasValidSimulation = (simulationId: string | undefined): boolean => {
  if (!simulationId) return false;
  // Check if the simulation exists in the simulations object and is a valid function
  return simulationId in simulations && typeof simulations[simulationId] === 'function';
};

// Add this helper function at the top level of the file, after the debounce function
const preloadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
  });
};

// Add this helper function at the top level of the file, after the preloadImage function
const getBiasImageUrl = (biasId: string, deviceType: 'desktop' | 'mobile', format: 'webp' | 'png' = 'png'): string => {
  // Special case mappings for known mismatches
  const specialCaseMap: Record<string, string> = {
    'dunning-kruger-effect': 'bias-dunning-kruger',
    'bias-dunning-kruger': 'bias-bias-dunning-kruger', // Handle duplicate prefix in filename
    'context-effect': 'bias-context-effect'
    // Add other special cases as needed
  };
  
  // Base filename without extension
  let baseFilename: string;
  
  // If this bias has a special case mapping, use that
  if (specialCaseMap[biasId]) {
    baseFilename = `/biases/${specialCaseMap[biasId]}-${deviceType}`;
  }
  // If the bias ID doesn't start with 'bias-', add it
  else if (!biasId.startsWith('bias-')) {
    baseFilename = `/biases/bias-${biasId}-${deviceType}`;
  }
  // Otherwise use the direct ID
  else {
    baseFilename = `/biases/${biasId}-${deviceType}`;
  }
  
  // Return URL with the requested format extension
  return `${baseFilename}.${format}`;
};

// Create a separate TestSection component
const TestSection: React.FC<{
  bias: CognitiveBias;
  currentQuestionIndex: number;
  answeredQuestions: Record<number, number>;
  onAnswerSubmit: (isCorrect: boolean, selectedOptionIndex?: number, questionIndex?: number) => void;
  onPrevious: () => void;
  onNext: () => void;
  isInTestSectionRef: React.MutableRefObject<boolean>;
}> = ({ 
  bias, 
  currentQuestionIndex, 
  answeredQuestions, 
  onAnswerSubmit,
  onPrevious,
  onNext,
  isInTestSectionRef
}) => {
  // Set the test section flag when this component is rendered
  React.useEffect(() => {
    // Set the flag directly
    isInTestSectionRef.current = true;
    return () => {
      isInTestSectionRef.current = false;
    };
  }, [isInTestSectionRef]);

  return (
    <div className="space-y-4 w-full overflow-visible">
      <div className="flex items-center justify-center text-center gap-3">
        <div className="bg-orange-100 p-2 rounded-lg">
          <Target className="w-5 h-5 text-orange-600" />
        </div>
        <h2 className="text-xl sm:text-2xl font-semibold text-center leading-tight hyphens-auto">Test Your Understanding</h2>
      </div>
      
      <p className="text-center text-gray-700 text-lg mb-6">
        Challenge yourself with these questions to see how well you understand this cognitive bias:
      </p>
      
      {bias.test?.questions?.length ? (
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-lg p-4 sm:p-6 md:p-8 border border-gray-100 w-full max-w-full mx-auto mb-4">
          <div className="text-sm text-gray-400 mb-6">
            Question {currentQuestionIndex + 1} of {bias.test.questions.length}
          </div>

          <div className="w-full overflow-visible px-3">
            <BiasTest 
              key={`test-${bias.id}-question-${currentQuestionIndex}`}
              test={{
                questions: [bias.test.questions[currentQuestionIndex]]
              }}
              variant="transparent"
              answeredQuestions={answeredQuestions}
              questionIndex={currentQuestionIndex}
              onAnswerSubmit={onAnswerSubmit}
            />
          </div>

          <div className="flex justify-between mt-8 mb-2 pt-2 pb-3">
            <button
              onClick={onPrevious}
              className="p-3 rounded-xl hover:bg-orange-50 transition-colors focus:outline-none"
              aria-label="Previous question"
            >
              <ChevronLeft className="w-6 h-6 text-gray-600" aria-hidden="true" />
            </button>
            <button
              onClick={onNext}
              className="p-3 rounded-xl hover:bg-orange-50 transition-colors focus:outline-none"
              aria-label="Next question"
            >
              <ChevronRight className="w-6 h-6 text-gray-600" aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-600">
          No test questions available.
        </div>
      )}
    </div>
  );
};

export const BiasPage: React.FC<BiasPageProps> = ({ biases }) => {
  const { biasId } = useParams();
  const [activeSection, setActiveSection] = useState<string>('introduction');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isContentLoaded, setIsContentLoaded] = useState(true);
  const [progress, setProgress] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState<Record<number, number>>({});
  const [testSectionProgress, setTestSectionProgress] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImageSrc, setLightboxImageSrc] = useState('');
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const navigate = useNavigate();
  
  const buttonsContainerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isClickScrolling = useRef(false);
  const isInTestSection = useRef(false);

  // Check if we're in prerendering mode
  const isPrerenderMode = typeof window !== 'undefined' && 
    ((window as any).__PRERENDER_INJECTED || 
     (window as any).__PRERENDERED || 
     navigator.userAgent.indexOf('Prerender') !== -1);

  // If we're in prerendering mode, we need to ensure the bias data is displayed immediately
  useEffect(() => {
    if (isPrerenderMode && biasId && biases.length > 0) {
      console.log(`[Prerender] Forcing immediate display of bias: ${biasId}`);
      const foundBias = biases.find(b => b?.id?.toString() === biasId);
      if (foundBias) {
        console.log('[Prerender] Found bias data, forcing render');
        setIsContentLoaded(true);
        
        // Dispatch an event to signal that the bias data is loaded
        const event = new CustomEvent('bias-data-loaded', { 
          detail: { biasId, success: true } 
        });
        document.dispatchEvent(event);
      }
    }
  }, [biasId, biases, isPrerenderMode]);

  // Check if we should force data loading
  const shouldForceDataLoad = isPrerenderMode && 
    (window as any).__PRERENDER_INJECTED?.forceDataLoad === true;

  // Memoize the current bias to prevent unnecessary re-renders
  const bias = useMemo(() => biases.find(b => b?.id?.toString() === biasId), [biasId, biases]);

  // Add an effect to force data loading during pre-rendering
  useEffect(() => {
    if (shouldForceDataLoad && !bias && biases.length > 0) {
      console.log('Force loading bias data during pre-rendering');
      // Dispatch a custom event to notify that we need to load bias data
      document.dispatchEvent(new CustomEvent('force-bias-data-load', {
        detail: { biasId }
      }));
    }
  }, [shouldForceDataLoad, bias, biases, biasId]);

  // Reset current question index when bias changes
  useEffect(() => {
    setCurrentQuestionIndex(0);
    setAnsweredQuestions({});
  }, [biasId]);

  // Add an effect to listen for force-bias-data-load events
  useEffect(() => {
    const handleForceBiasDataLoad = (event: Event) => {
      const customEvent = event as CustomEvent;
      const forcedBiasId = customEvent.detail?.biasId;
      
      if (forcedBiasId && isPrerenderMode) {
        console.log(`Force loading bias data for: ${forcedBiasId}`);
        // If we're in prerendering mode, we need to ensure the bias data is loaded
        // This is a fallback mechanism for when the initial data fetch doesn't work
        if (!bias && biases.length > 0) {
          const foundBias = biases.find(b => b?.id?.toString() === forcedBiasId);
          if (foundBias) {
            console.log('Found bias data, forcing render');
            // We found the bias, now we need to force a render
            setIsContentLoaded(true);
          }
        }
      }
    };

    // Add event listener
    document.addEventListener('force-bias-data-load', handleForceBiasDataLoad);

    // Clean up
    return () => {
      document.removeEventListener('force-bias-data-load', handleForceBiasDataLoad);
    };
  }, [bias, biases, isPrerenderMode]);

  // Pre-compute related biases
  const relatedBiases = useMemo(() => {
    if (!bias) return [];
    return bias.related?.length 
      ? bias.related.map(id => biases.find(b => b.id === id)).filter(Boolean)
      : getRandomBiasesFromCategory(bias, biases);
  }, [bias, biases]);

  // Calculate progress whenever active section changes
  const calculateProgress = useCallback((sectionId: string) => {
    if (!buttonsContainerRef.current) return 0;

    const container = buttonsContainerRef.current;
    const buttons = Array.from(container.children) as HTMLElement[];
    
    const activeIndex = buttons.findIndex(
      (btn) => btn.getAttribute('data-section') === sectionId
    );
    if (activeIndex === -1) return 0;

    const activeButton = buttons[activeIndex];
    const containerRect = container.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();

    const progress = ((buttonRect.right - containerRect.left) / containerRect.width) * 100;
    return Math.min(progress, 100);
  }, []);

  // Add this helper function to calculate mobile progress
  const calculateMobileProgress = useCallback((sectionId: string) => {
    if (!bias) return 0;
    
    // Use a direct calculation instead of depending on mainSections
    const allSections = ['introduction', 'examples', 'prevention', 'test'];
    const filteredSections = allSections.filter(section => {
      if (section === 'test' && !bias.test) return false;
      if (section === 'prevention' && !bias.howToOvercome?.length) return false;
      if (section === 'examples' && !bias.examples?.length) return false;
      return true;
    });
    
    const sectionIndex = filteredSections.findIndex(s => s === sectionId);
    if (sectionIndex === -1) return 0;
    
    // Calculate progress based on section index
    return ((sectionIndex + 1) / filteredSections.length) * 100;
  }, [bias]);

  // Add a handler for answer submission
  const handleAnswerSubmit = useCallback((isCorrect: boolean, selectedOptionIndex?: number, questionIndex?: number) => {
    if (questionIndex !== undefined && selectedOptionIndex !== undefined) {
      // Set flags to prevent progress bar updates during answer submission
      isClickScrolling.current = true;
      isInTestSection.current = true;
      
      // Directly set active section to 'test' to prevent it from changing
      if (activeSection === 'test') {
        // Store the current progress
        const currentProgress = progress;
        
        setAnsweredQuestions(prev => ({
          ...prev,
          [questionIndex]: selectedOptionIndex
        }));
        
        // Force the active section to stay as 'test' and restore progress
        setTimeout(() => {
          setActiveSection('test');
          setProgress(currentProgress);
        }, 0);
      } else {
        setAnsweredQuestions(prev => ({
          ...prev,
          [questionIndex]: selectedOptionIndex
        }));
      }
      
      // Reset the click scrolling flag after a short delay
      setTimeout(() => {
        isClickScrolling.current = false;
      }, TRANSITION_DURATION);
    }
  }, [activeSection, progress]);

  // Define navigation handlers
  const handlePreviousQuestion = useCallback(() => {
    if (!bias?.test?.questions) return;
    
    // Set flags to prevent progress bar updates during navigation
    isClickScrolling.current = true;
    isInTestSection.current = true;
    
    // Store the current progress if in test section
    const currentProgress = progress;
    const isInTest = activeSection === 'test';
    
    setCurrentQuestionIndex(current => {
      if (current === 0) {
        return bias.test!.questions.length - 1;
      }
      return current - 1;
    });
    
    // Force the active section to stay as 'test' and restore progress
    if (isInTest) {
      setTimeout(() => {
        setActiveSection('test');
        setProgress(currentProgress);
      }, 0);
    }
    
    // Reset the click scrolling flag after a short delay
    setTimeout(() => {
      isClickScrolling.current = false;
    }, TRANSITION_DURATION);
  }, [bias, activeSection, progress]);

  const handleNextQuestion = useCallback(() => {
    if (!bias?.test?.questions) return;
    
    // Set flags to prevent progress bar updates during navigation
    isClickScrolling.current = true;
    isInTestSection.current = true;
    
    // Store the current progress if in test section
    const currentProgress = progress;
    const isInTest = activeSection === 'test';
    
    setCurrentQuestionIndex(current => {
      if (current === bias.test!.questions.length - 1) {
        return 0;
      }
      return current + 1;
    });
    
    // Force the active section to stay as 'test' and restore progress
    if (isInTest) {
      setTimeout(() => {
        setActiveSection('test');
        setProgress(currentProgress);
      }, 0);
    }
    
    // Reset the click scrolling flag after a short delay
    setTimeout(() => {
      isClickScrolling.current = false;
    }, TRANSITION_DURATION);
  }, [bias, activeSection, progress]);

  // Memoize metadata
  const metadata = useMemo(() => {
    if (!bias) return null;
    
    // Create the base structured data
    const baseStructuredData = generateBiasStructuredData(bias, biasId || '');
    
    // Parse the existing structured data
    let structuredData;
    try {
      structuredData = JSON.parse(baseStructuredData);
      
      // Add image structured data
      structuredData.image = {
        "@type": "ImageObject",
        "url": `${SITE_URL}${getBiasImageUrl(bias.id, 'desktop', 'webp')}`,
        "width": "800",
        "height": "500",
        "caption": `Visual representation of ${bias.name}`,
        "contentUrl": `${SITE_URL}${getBiasImageUrl(bias.id, 'desktop', 'webp')}`,
        "encodingFormat": "image/webp"
      };
      
    } catch (e) {
      // If parsing fails, use the original data
      structuredData = baseStructuredData;
    }
    
    return {
      metaDescription: generateBiasDescription(bias),
      pageTitle: generateBiasTitle(bias),
      keywords: generateBiasKeywords(bias),
      structuredData: typeof structuredData === 'string' 
        ? structuredData 
        : JSON.stringify(structuredData)
    };
  }, [bias, biasId]);

  // Define sections early
  const mainSections = useMemo(() => {
    if (!bias) return [];
    return [
      {
        id: 'introduction',
        title: 'Introduction',
        icon: Book,
        content: (
          <LearningBlock 
            title={`Understanding ${bias.name}`} 
            icon={Brain} 
            titleClassName="text-xl sm:text-2xl font-semibold text-center leading-tight hyphens-auto"
            useH1={true}
          >
            <KeyPoint 
              title={bias.name} 
              className="mb-8 p-6 border border-orange-200 rounded-lg shadow-sm bg-orange-50/30"
            >
              <MarkdownContent content={bias.short_description} />
            </KeyPoint>
            
            <MarkdownContent content={bias.long_description} className="mt-6" />
            
            {/* Image section - conditionally rendered */}
            <div className="mt-6 mb-8">
              <div 
                className="rounded-xl shadow-sm overflow-hidden cursor-pointer"
                id={`bias-image-container-${bias.id}`}
              >
                {/* Desktop image - hidden on mobile */}
                <picture className="w-full hidden md:block">
                  <source 
                    srcSet={getBiasImageUrl(bias.id, 'desktop', 'webp')} 
                    type="image/webp" 
                  />
                  <img 
                    src={getBiasImageUrl(bias.id, 'desktop', 'png')} 
                    alt={`Diagram illustrating how ${bias.name} affects decision-making processes`}
                    className="w-full"
                    onClick={() => openLightbox(getBiasImageUrl(bias.id, 'desktop', 'webp'), getBiasImageUrl(bias.id, 'desktop', 'png'))}
                    loading="eager" 
                    decoding="async"
                    width="800"
                    height="500"
                    title={`Visual explanation of ${bias.name}`}
                    itemProp="image"
                    onError={(e) => {
                      // Hide the container if all images fail to load
                      const container = document.getElementById(`bias-image-container-${bias.id}`);
                      const mobileImg = container?.querySelector('.md\\:hidden');
                      if (mobileImg && getComputedStyle(mobileImg).display === 'none') {
                        if (container) container.style.display = 'none';
                        const caption = document.getElementById(`bias-image-caption-${bias.id}`);
                        if (caption) caption.style.display = 'none';
                      }
                    }}
                  />
                </picture>
                {/* Mobile image - hidden on desktop */}
                <picture className="w-full md:hidden">
                  <source 
                    srcSet={getBiasImageUrl(bias.id, 'mobile', 'webp')} 
                    type="image/webp" 
                  />
                  <img 
                    src={getBiasImageUrl(bias.id, 'mobile', 'png')} 
                    alt={`Diagram illustrating how ${bias.name} affects decision-making processes`}
                    className="w-full"
                    onClick={() => openLightbox(getBiasImageUrl(bias.id, 'mobile', 'webp'), getBiasImageUrl(bias.id, 'mobile', 'png'))}
                    loading="eager"
                    decoding="async"
                    width="400" 
                    height="400"
                    title={`Visual explanation of ${bias.name}`}
                    itemProp="image"
                    onError={(e) => {
                      // Hide the container if all images fail to load
                      const container = document.getElementById(`bias-image-container-${bias.id}`);
                      const desktopImg = container?.querySelector('.hidden.md\\:block');
                      if (desktopImg && getComputedStyle(desktopImg).display === 'none') {
                        if (container) container.style.display = 'none';
                        const caption = document.getElementById(`bias-image-caption-${bias.id}`);
                        if (caption) caption.style.display = 'none';
                      }
                    }}
                  />
                </picture>
              </div>
              <p 
                id={`bias-image-caption-${bias.id}`}
                className="text-center text-gray-600 mt-2 italic"
                itemProp="caption"
              >
                Visual representation of {bias.name} (click to enlarge)
              </p>
            </div>
          </LearningBlock>
        )
      },
      {
        id: 'examples',
        title: 'Examples',
        icon: History,
        content: (
          <LearningBlock 
            title={`Examples of ${bias.name}`} 
            icon={History} 
            titleClassName="text-xl sm:text-2xl font-semibold text-center leading-tight hyphens-auto"
            useH1={false}
          >
            <div className="prose max-w-none">
              <p className="text-center text-gray-700 text-lg mb-6">
                Here are some real-world examples that demonstrate how this bias affects our thinking:
              </p>
              
              <div className="space-y-6">
                {/* Show simulation only if it exists in the simulations list */}
                {bias.simulationComponent && hasValidSimulation(bias.simulationComponent) && (
                  <div className="border border-orange-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 hover:border-orange-300">
                    <div className="bg-orange-50 px-6 py-3 border-b border-orange-200 flex items-center justify-between rounded-t-xl">
                      <h3 className="font-semibold text-gray-800 text-lg">
                        {getSimulationTitle(bias.simulationComponent)}
                      </h3>
                      
                      {/* Interactive label - without icon */}
                      <div className="bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                        Interactive
                      </div>
                    </div>
                    <div className="p-6">
                      <BiasSimulation simulation={bias.simulationComponent} />
                    </div>
                  </div>
                )}

                {/* Then show other examples */}
                {bias.examples?.map((example, index) => (
                  <div key={index} className="border border-orange-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 hover:border-orange-300">
                    <div className="bg-orange-50 px-6 py-3 border-b border-orange-200 rounded-t-xl">
                      <h3 className="font-semibold text-gray-800 text-lg">{example.title}</h3>
                    </div>
                    <div className="p-6">
                      <MarkdownContent content={example.description} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </LearningBlock>
        )
      },
      {
        id: 'prevention',
        title: 'Prevention',
        icon: Lightbulb,
        content: (
          <LearningBlock 
            title={`How to Overcome ${bias.name}`} 
            icon={Lightbulb} 
            titleClassName="text-xl sm:text-2xl font-semibold text-center leading-tight hyphens-auto"
            useH1={false}
          >
            <div className="prose max-w-none mb-6">
              <p className="text-center text-gray-700 text-lg">Here are strategies to help you recognize and overcome this bias:</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {bias.howToOvercome?.map((strategy, index) => (
                <div 
                  key={index} 
                  className="border border-orange-200 rounded-xl p-6 transition-all duration-300
                    shadow-sm hover:shadow-md bg-white
                    hover:border-orange-300"
                >
                  <CheckItem 
                    title={strategy.title}
                    description={<MarkdownContent content={strategy.strategy} />}
                  />
                </div>
              ))}
            </div>
          </LearningBlock>
        )
      },
      {
        id: 'test',
        title: 'Test',
        icon: Target,
        content: (
          <TestSection 
            bias={bias}
            currentQuestionIndex={currentQuestionIndex}
            answeredQuestions={answeredQuestions}
            onAnswerSubmit={handleAnswerSubmit}
            onPrevious={handlePreviousQuestion}
            onNext={handleNextQuestion}
            isInTestSectionRef={isInTestSection}
          />
        )
      }
    ].filter(section => {
      if (section.id === 'test' && !bias.test) return false;
      if (section.id === 'prevention' && !bias.howToOvercome?.length) return false;
      if (section.id === 'examples' && !bias.examples?.length) return false;
      return true;
    });
  }, [bias, currentQuestionIndex, answeredQuestions, handleAnswerSubmit, handlePreviousQuestion, handleNextQuestion]);

  const additionalSections = useMemo(() => {
    if (!bias) return [];
    return [
      {
        id: 'related',
        title: 'Related',
        icon: Network,
        content: <RelatedBiases biases={relatedBiases.filter((b): b is CognitiveBias => b !== null && b !== undefined)} />
      },
      {
        id: 'references',
        title: 'References',
        icon: BookOpen,
        content: (
          <div className="space-y-4">
            <div className="flex items-center justify-center text-center gap-3">
              <div className="bg-orange-100 p-2 rounded-lg">
                <BookOpen className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-xl sm:text-2xl font-semibold text-center leading-tight hyphens-auto">Academic References</h2>
            </div>
            
            <div className="bg-white rounded-2xl p-6 border border-orange-200 shadow-sm hover:shadow-md transition-all duration-300 hover:border-orange-300">
              <ul className="space-y-4">
                {bias.references?.map((reference, index) => (
                  <li key={index} className="flex gap-3">
                    <span className="text-orange-500 mt-1 h-2.5 w-2.5 rounded-full bg-orange-500 shrink-0" />
                    <span className="text-gray-600 break-words">{reference}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )
      }
    ].filter(section => {
      if (section.id === 'related') {
        return relatedBiases.length > 0;
      }
      if (section.id === 'references' && !bias?.references?.length) return false;
      return true;
    });
  }, [bias, relatedBiases]);

  const sections = useMemo(() => [...mainSections, ...additionalSections], [mainSections, additionalSections]);

  // Define scroll function
  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (!element) return;

    isClickScrolling.current = true;
    setActiveSection(sectionId);
    
    // Set the test section flag if we're scrolling to the test section
    isInTestSection.current = sectionId === 'test';

    // Set progress based on viewport width
    const isMobile = window.innerWidth < 768; // md breakpoint
    setProgress(isMobile ? calculateMobileProgress(sectionId) : calculateProgress(sectionId));

    const elementRect = element.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + window.pageYOffset;
    const headerOffset = 120;
    const targetPosition = absoluteElementTop - headerOffset;

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });

    setTimeout(() => {
      isClickScrolling.current = false;
    }, TRANSITION_DURATION);
  }, [calculateProgress, calculateMobileProgress]);

  // Preload critical content effect
  useEffect(() => {
    if (!isContentLoaded) {
      const preloadContent = async () => {
        await new Promise(resolve => setTimeout(resolve, 100));
        setIsContentLoaded(true);
      };
      preloadContent();
    }
  }, [isContentLoaded]);

  // Update the intersection observer to handle both desktop and mobile
  useEffect(() => {
    if (!bias) return;

    const debouncedSetActiveSection = debounce((sectionId: string) => {
      if (!isClickScrolling.current) {
        // Only update active section if it's different from the current one
        // or if it's not the test section (to prevent progress bar reset during test interactions)
        if (activeSection !== sectionId || (sectionId !== 'test' && activeSection !== sectionId)) {
          // Update isInTestSection flag when changing sections
          if (sectionId !== 'test') {
            isInTestSection.current = false;
          } else if (sectionId === 'test') {
            isInTestSection.current = true;
          }
          
          setActiveSection(sectionId);
          const isMobile = window.innerWidth < 768;
          setProgress(isMobile ? calculateMobileProgress(sectionId) : calculateProgress(sectionId));
        }
      }
    }, TRANSITION_DURATION);

    const observers = sectionRefs.current.map((ref, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !isClickScrolling.current) {
              const sectionId = sections[index].id;
              const mainSectionIndex = mainSections.findIndex(s => s.id === sectionId);
              
              // Only trigger section change if it's a significant intersection
              if (mainSectionIndex !== -1 && entry.intersectionRatio >= 0.2) {
                // If we're in the test section and interacting with it, don't update progress
                if (sectionId === 'test' && activeSection === 'test' && 
                    (answeredQuestions[currentQuestionIndex] !== undefined || 
                     document.activeElement?.closest('.test-section'))) {
                  return;
                }
                
                debouncedSetActiveSection(sectionId);
              }
            }
          });
        },
        {
          threshold: [0, 0.1, 0.2, 0.3, 0.4],
          rootMargin: '-120px 0px -40% 0px'
        }
      );

      if (ref) {
        observer.observe(ref);
      }

      return observer;
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [bias, sections, mainSections, calculateProgress, calculateMobileProgress, activeSection, answeredQuestions, currentQuestionIndex]);

  // Add an effect to detect when the user enters the test section
  useEffect(() => {
    if (activeSection === 'test') {
      isInTestSection.current = true;
    }
  }, [activeSection]);

  // Update the initialization effect
  useEffect(() => {
    if (!bias) return;
    
    setActiveSection('introduction');
    isInTestSection.current = false;
    const isMobile = window.innerWidth < 768;
    requestAnimationFrame(() => {
      setProgress(isMobile ? calculateMobileProgress('introduction') : calculateProgress('introduction'));
    });
  }, [bias, calculateProgress, calculateMobileProgress]);

  // Update the openLightbox function to support WebP with PNG fallback
  const openLightbox = (webpSrc: string, pngFallbackSrc: string) => {
    // Modern browsers will use WebP, others will use the fallback
    if (typeof HTMLPictureElement !== 'undefined') {
      setLightboxImageSrc(webpSrc);
    } else {
      setLightboxImageSrc(pngFallbackSrc);
    }
    setIsLightboxOpen(true);
  };

  // Add a function to close the lightbox
  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  // Get share text for the bias
  const getShareText = useCallback(() => {
    if (!bias) return 'Check out this cognitive bias';
    
    return `Learn about ${bias.name}, a cognitive bias that affects how we think and make decisions. ${bias.short_description?.substring(0, 100)}...`;
  }, [bias]);

  // Get share URL for the bias
  const getShareUrl = useCallback(() => {
    return `${SITE_URL}/bias/${biasId}/`;
  }, [biasId]);

  return (
    <>
      <Helmet prioritizeSeoTags>
        {metadata && (
          <>
            <title>{metadata.pageTitle}</title>
            <meta name="description" content={metadata.metaDescription} key="description" />
            <meta name="keywords" content={metadata.keywords} key="keywords" />
            
            {/* OpenGraph tags */}
            <meta property="og:title" content={metadata.pageTitle} key="og:title" />
            <meta property="og:description" content={metadata.metaDescription} key="og:description" />
            <meta property="og:type" content="article" key="og:type" />
            <meta property="og:url" content={`${SITE_URL}/bias/${biasId}/`} key="og:url" />
            <meta property="og:site_name" content="Cognitive Bias Lab" key="og:site_name" />
            
            {/* Dynamic bias image for OG tags instead of logo */}
            <meta property="og:image" content={`${SITE_URL}${getBiasImageUrl(bias?.id ?? 'default', 'desktop', 'webp')}`} key="og:image" />
            <meta property="og:image:alt" content={`Diagram illustrating how ${bias?.name ?? 'cognitive bias'} affects decision-making processes`} key="og:image:alt" />
            <meta property="og:image:width" content="800" key="og:image:width" />
            <meta property="og:image:height" content="500" key="og:image:height" />
            <meta property="og:image:type" content="image/webp" key="og:image:type" />
            
            {/* Fallback PNG for platforms that don't support WebP */}
            <meta property="og:image" content={`${SITE_URL}${getBiasImageUrl(bias?.id ?? 'default', 'desktop', 'png')}`} key="og:image:fallback" />
            <meta property="og:image:type" content="image/png" key="og:image:fallback:type" />
            
            {/* Canonical link */}
            <link rel="canonical" href={`${SITE_URL}/bias/${biasId}/`} key="canonical" data-source="page-component" />
            
            {/* Twitter Card tags */}
            <meta name="twitter:card" content="summary_large_image" key="twitter:card" />
            <meta name="twitter:title" content={metadata.pageTitle} key="twitter:title" />
            <meta name="twitter:description" content={metadata.metaDescription} key="twitter:description" />
            <meta name="twitter:image" content={`${SITE_URL}${getBiasImageUrl(bias?.id ?? 'default', 'desktop', 'webp')}`} key="twitter:image" />
            <meta name="twitter:image:alt" content={`Diagram illustrating how ${bias?.name ?? 'cognitive bias'} affects decision-making processes`} key="twitter:image:alt" />
            
            {/* Add unique identifier to help with SEO tracking */}
            <meta name="page-type" content="bias-page" />
            <meta name="bias-id" content={biasId} />
            <meta name="bias-category" content={bias?.category ?? 'general'} />
            
            {/* Article-specific structured data */}
            <script type="application/ld+json">{metadata.structuredData}</script>
          </>
        )}
      </Helmet>
      
      {/* Use the new Lightbox component */}
      <Lightbox 
        isOpen={isLightboxOpen}
        onClose={closeLightbox}
        imageSrc={lightboxImageSrc}
        alt={`Enlarged view of ${bias?.name}`}
        title={`Visual diagram explaining ${bias?.name}`}
      />
      
      {/* Use the reusable ShareModal component */}
      <ShareModal 
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        shareUrl={getShareUrl()}
        shareText={getShareText()}
        title={`Share ${bias?.name}`}
      />
      
      {/* Add data-page-type attribute for tracking */}
      <div className="bias-page" data-page-type="bias-page" data-bias-id={biasId} data-bias-category={bias?.category}>
        {/* Floating progress header - changed h1 to h2 since we now have a direct h1 */}
        <div className="sticky top-4 z-30 mt-8">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {/* Enhanced sticky header styling */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200">
                <div className="flex items-center justify-between p-4">
                  <h2 className="text-xl font-semibold text-gray-800">{bias?.name ?? 'Cognitive Bias'}</h2>
                  
                  {/* Mobile share button - visible only on mobile */}
                  <div className="md:hidden">
                    <button
                      onClick={() => setIsShareModalOpen(true)}
                      className="p-2 rounded-full hover:bg-orange-50 text-gray-600 hover:text-orange-500 transition-colors"
                      aria-label="Share this article"
                    >
                      <Share2 className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Desktop version */}
                  <div className="flex-1 hidden md:flex justify-end items-center">
                    <div 
                      className="flex gap-4 relative" 
                      ref={buttonsContainerRef}
                      role="navigation"
                      aria-label="Content sections"
                    >
                      {mainSections.map((section) => (
                        <button
                          key={section.id}
                          data-section={section.id}
                          onClick={() => scrollToSection(section.id)}
                          aria-label={`Go to ${section.title} section`}
                          aria-current={activeSection === section.id ? 'true' : undefined}
                          className={`
                            px-6 py-2 
                            rounded-xl 
                            flex items-center 
                            justify-center 
                            gap-2 
                            min-w-[120px]
                            will-change-transform
                            focus:outline-none
                            ${activeSection === section.id 
                              ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-md hover:shadow-lg'
                              : 'hover:bg-orange-100/50 text-gray-600 hover:text-gray-800'
                            }
                          `}
                        >
                          <section.icon className="w-4 h-4" aria-hidden="true" />
                          <span>{section.title}</span>
                        </button>
                      ))}
                      
                      {/* Desktop progress bar */}
                      <div 
                        className="absolute -bottom-3 left-0 w-full h-1 bg-gray-100 overflow-hidden"
                        role="progressbar"
                        aria-valuenow={progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label="Reading progress"
                      >
                        <div 
                          className="h-full bg-gradient-to-r from-orange-500 to-pink-500 will-change-transform"
                          style={{ 
                            width: `${progress}%`,
                            transition: `width ${TRANSITION_DURATION}ms ease-in-out`
                          }}
                        />
                      </div>
                    </div>
                    
                    {/* Desktop share button - only visible on desktop */}
                    <button
                      onClick={() => setIsShareModalOpen(true)}
                      className="p-2 ml-4 rounded-full hover:bg-orange-50 text-gray-600 hover:text-orange-500 transition-colors"
                      aria-label="Share this article"
                    >
                      <Share2 className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Mobile progress bar */}
                  <div className="md:hidden w-full absolute bottom-0 left-0 h-1 bg-gray-100 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-orange-500 to-pink-500 will-change-transform"
                      style={{ 
                        width: `${progress}%`,
                        transition: `width ${TRANSITION_DURATION}ms ease-in-out`
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content - with min-height to prevent layout shifts */}
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Solid background and increased padding */}
            <div className="bg-white backdrop-blur-sm rounded-3xl shadow-lg border border-gray-100 
              p-6 sm:p-8 md:p-10 min-h-[800px]" 
              style={{ contain: 'layout paint' }}
            >
              {/* Removed space-y, added HR between sections */}
              <div className="max-w-full">
                {sections.map((section, index) => (
                  <React.Fragment key={section.id}>
                    {/* Add HR before section if it's not the first */}
                    {index > 0 && <hr className="my-12 border-gray-200" />}
                    <div 
                      id={section.id}
                      ref={el => sectionRefs.current[index] = el}
                      className={`scroll-mt-32 pb-4 ${section.id === 'test' ? 'overflow-visible' : 'overflow-hidden'}`}
                      style={{ 
                        minHeight: section.id === 'examples' ? '400px' : 'auto',
                        contain: section.id === 'test' ? 'none' : 'content',
                        padding: section.id === 'test' ? '0 8px' : '0'
                      }}
                    >
                      {/* Only use Suspense for non-critical sections */}
                      {(section.id === 'test' || section.id === 'examples') && !isContentLoaded ? (
                        <Suspense fallback={
                          <div className="animate-pulse bg-gray-100 rounded-xl h-64 w-full" />
                        }>
                          {section.content}
                        </Suspense>
                      ) : (
                        section.content
                      )}
                    </div>
                  </React.Fragment>
                ))}
                {!isContentLoaded && (
                  <div className="space-y-8 mt-8">
                    <div className="animate-pulse bg-gray-100 h-4 w-full rounded-xl" />
                    <div className="animate-pulse bg-gray-100 h-4 w-5/6 rounded-xl" />
                    <div className="animate-pulse bg-gray-100 h-4 w-4/6 rounded-xl" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Add the floating chat button if we have bias data */}
      {bias && <BiasChatButton bias={bias} />}
    </>
  );
}; 